/**
 * Chat settings — preferences that belong to *you*, not to one conversation.
 *
 * Per-conversation appearance lives on `Conversation.theme` (see
 * chat-theme-settings.tsx). This store is the other half: how you show up
 * across every chat, and how the app behaves for you.
 *
 * Persisted to localStorage and read synchronously on first render so your
 * colour never flashes the fallback on load.
 */

import { create } from "zustand";
import { updateProfile, useUserStore } from "@slumbr/core";
import type { NameEffect } from "../types";

const STORAGE_KEY = "slumbr:messenger:chatSettings";

export interface ChatSettings {
  /** Your personal colour — tints your name, your avatar ring and your own
   *  accents wherever you appear, for everyone. Saved to your profile so the
   *  people you chat with see it too; kept here as well so your own client
   *  updates the instant you pick it. `null` = derive one from your user id. */
  myChatColor: string | null;
  /** Second gradient stop for your name. */
  myChatColorAlt: string | null;
  /** How your name renders: flat, gradient, glowing, or both. */
  myNameEffect: NameEffect;
  /** Play the chat ping for new messages. Mirrored into the user's muted-apps
   *  list so the realtime handler honours it. */
  soundEnabled: boolean;
  /** Give each person their own name colour in group chats. Off renders
   *  everyone in neutral. Purely cosmetic — no layout impact. */
  colorfulNames: boolean;
}

const DEFAULTS: ChatSettings = {
  myChatColor: null,
  myChatColorAlt: null,
  myNameEffect: "none",
  soundEnabled: true,
  colorfulNames: true,
};

function load(): ChatSettings {
  try {
    if (typeof window === "undefined") return DEFAULTS;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<ChatSettings>) };
  } catch {
    return DEFAULTS;
  }
}

function save(settings: ChatSettings) {
  try {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage full or blocked — the setting just won't survive a reload.
  }
}

/** Keep the shared notification prefs in step with the chat sound toggle. */
function syncMutedApps(soundEnabled: boolean) {
  const store = useUserStore.getState();
  const prefs = store.user?.preferences.notifications;
  if (!prefs) return;
  const muted = new Set(prefs.mutedApps);
  if (soundEnabled) muted.delete("messenger");
  else muted.add("messenger");
  store.updatePreferences({
    notifications: { ...prefs, mutedApps: [...muted] },
  });
}

interface ChatSettingsStore extends ChatSettings {
  setMyChatColor: (color: string | null) => void;
  setMyNameStyle: (next: { colorAlt?: string | null; effect?: NameEffect }) => void;
  adoptServerColor: (color: string | undefined | null) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setColorfulNames: (on: boolean) => void;
  reset: () => void;
}

export const useChatSettingsStore = create<ChatSettingsStore>((set, get) => ({
  ...load(),

  setMyChatColor: (myChatColor) => {
    set({ myChatColor });
    save({ ...get(), myChatColor });
    // Publish to the profile so every other participant picks it up on their
    // next conversation read. Local state already updated, so a failed write
    // costs the user nothing but a retry.
    void updateProfile({ chatColor: myChatColor }).catch((err) => {
      console.warn("[Chat] failed to save chat colour", err);
    });
  },

  setMyNameStyle: ({ colorAlt, effect }) => {
    const next = {
      ...get(),
      ...(colorAlt !== undefined ? { myChatColorAlt: colorAlt } : {}),
      ...(effect !== undefined ? { myNameEffect: effect } : {}),
    };
    set({ myChatColorAlt: next.myChatColorAlt, myNameEffect: next.myNameEffect });
    save(next);
    void updateProfile({
      chatColorAlt: next.myChatColorAlt,
      chatNameEffect: next.myNameEffect,
    }).catch((err) => {
      console.warn("[Chat] failed to save name style", err);
    });
  },

  /** Take the server's colour when this device has never set one. */
  adoptServerColor: (color) => {
    if (!color || get().myChatColor) return;
    set({ myChatColor: color });
    save({ ...get(), myChatColor: color });
  },

  setSoundEnabled: (soundEnabled) => {
    set({ soundEnabled });
    save({ ...get(), soundEnabled });
    syncMutedApps(soundEnabled);
  },

  setColorfulNames: (colorfulNames) => {
    set({ colorfulNames });
    save({ ...get(), colorfulNames });
  },

  reset: () => {
    set({ ...DEFAULTS });
    save(DEFAULTS);
    syncMutedApps(DEFAULTS.soundEnabled);
    void updateProfile({ chatColor: null, chatColorAlt: null, chatNameEffect: "none" }).catch(
      () => {}
    );
  },
}));

/** Non-reactive read for callbacks and non-React code. */
export function getMyChatColor(): string | null {
  return useChatSettingsStore.getState().myChatColor;
}
